import { InsuranceSubscriberRelationship } from 'domains/client';
import { PayerTypes } from 'domains/signup/data/types';
import { CommonFieldsSchema, getAgeValidation } from 'domains/signupDirect/schemas/common';
import { PaymentMethod } from 'domains/signupDirect/schemas/filtersSchema';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { isUndefined } from 'lodash';
import { TypeInsuranceFields } from 'types/contentful';
import { z } from 'zod';
import { EvaluationScheduleSchema } from './evaluationScheduleSchema';
import { RequiredErrorMessage } from 'utils/validators/zodHelpers';
import { EmailSchema, PasswordSchema } from 'utils/validators/commonSchemas';
import { signupPCPSchema } from '../Evaluation/Insurance/PCP/pcpSchemas';
import { signupCanBookEvaluation } from '../businessLogic/canBookEvaluation';
import { SignupError } from 'utils/errors/debuggableError';

export enum PaymentMethodType {
  PRIVATE_PAY = 'privatePay',
  MEDICAID = 'insurance-medicaid',
  MEDICARE = 'insurance-medicare',
  COMMERCIAL = 'insurance-commercial',
}

const InsuranceInfo = z.object({
  insuranceData: PaymentMethod.options[0].pick({
    insuranceData: true,
  }).shape.insuranceData,
  memberId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  groupId: z.string(RequiredErrorMessage).optional(),
  eligibilityCheckResult: z.nativeEnum(EligibilityCheckResult).optional(),
  preApprovedEligibilityCheck: z.boolean().optional(),
});

const PrimaryInsuranceSubscriber = z
  .object({
    relationShipToClient: z.nativeEnum(InsuranceSubscriberRelationship, RequiredErrorMessage),
    dateOfBirth: getAgeValidation(true),
    firstName: z.string(RequiredErrorMessage).optional(),
    lastName: z.string(RequiredErrorMessage).optional(),
  })
  .refine(
    data => {
      const isSelf = data.relationShipToClient === InsuranceSubscriberRelationship.Self;
      const isValidDOB = !isUndefined(data.dateOfBirth);
      return isSelf ? true : isValidDOB;
    },
    {
      message: RequiredErrorMessage.message,
      path: ['dateOfBirth'],
    },
  )
  .refine(data => (data.relationShipToClient !== InsuranceSubscriberRelationship.Self ? data.firstName : true), {
    message: RequiredErrorMessage.message,
    path: ['firstName'],
  })
  .refine(data => (data.relationShipToClient !== InsuranceSubscriberRelationship.Self ? data.lastName : true), {
    message: RequiredErrorMessage.message,
    path: ['lastName'],
  });

export type PrimaryInsuranceSubscriberType = Required<z.infer<typeof PrimaryInsuranceSubscriber>>;

// Shared CC info
const CCPaymentInfo = z.object({
  stripeMethodId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
});

// Medicaid
export const MedicaidBilling = InsuranceInfo.extend({
  type: z.literal(PaymentMethodType.MEDICAID),
});

// Commercial Insurance
export const CommercialInsuranceBilling = InsuranceInfo.extend({
  type: z.literal(PaymentMethodType.COMMERCIAL),
  stripeMethodId: CCPaymentInfo.shape.stripeMethodId.optional(),
  subscriber: PrimaryInsuranceSubscriber,
});

// Medicare
export const MedicareBilling = InsuranceInfo.extend({
  type: z.literal(PaymentMethodType.MEDICARE),
  stripeMethodId: CCPaymentInfo.shape.stripeMethodId.optional(),
});

// Private Pay billing
export const PrivatePayBilling = CCPaymentInfo.extend({
  type: z.literal(PaymentMethodType.PRIVATE_PAY),
});

export const BillingInfo = z
  .discriminatedUnion('type', [PrivatePayBilling, MedicaidBilling, MedicareBilling, CommercialInsuranceBilling])
  .refine(
    // note(juan): Conditionally check the credit card field ONLY when eligibility is OK and context requires a CC
    data => {
      return signupCanBookEvaluation({
        billingInfoType: data.type,
        eligibilityResult: 'eligibilityCheckResult' in data ? data.eligibilityCheckResult : undefined,
        hasEnteredCCInfo: 'stripeMethodId' in data && !!data.stripeMethodId,
        isPreApprovedEligibility: 'preApprovedEligibilityCheck' in data ? data.preApprovedEligibilityCheck : undefined,
      });
    },
    {
      message: RequiredErrorMessage.message,
      path: ['stripeMethodId'],
    },
  );

const ExistingContactInfo = z.object({
  isAlreadyContact: z.literal(true),
  contactId: z.string(),
  email: EmailSchema,
});

const NewContactInfo = z.object({
  isAlreadyContact: z.literal(false),
  email: EmailSchema,
  newPassword: PasswordSchema,
});

export const LoginContactInfo = z.discriminatedUnion('isAlreadyContact', [ExistingContactInfo, NewContactInfo]);
export type LoginContactInfoType = z.infer<typeof LoginContactInfo>;

// Form schemas
export const EvaluationForm = CommonFieldsSchema.extend({
  loginContactInfo: LoginContactInfo,
  evaluationSchedule: EvaluationScheduleSchema,
  billingInfo: BillingInfo,
  leadId: z.string().optional(),
  insuranceLeadId: z.string().optional(),
  consentToTerms: z.boolean().optional(),
  primaryPhysician: signupPCPSchema.and(z.object({ isPCPRequired: z.boolean() })),
});

export type EvaluationFormType = z.infer<typeof EvaluationForm>;

export const paymentMethodIsInsurance = (paymentData: EvaluationFormType['billingInfo']) => {
  return (
    paymentData.type === PaymentMethodType.COMMERCIAL ||
    paymentData.type === PaymentMethodType.MEDICAID ||
    paymentData.type === PaymentMethodType.MEDICARE
  );
};

export const paymentTypeIsInsurance = (type: EvaluationFormType['billingInfo']['type']) => {
  return (
    type === PaymentMethodType.COMMERCIAL || type === PaymentMethodType.MEDICAID || type === PaymentMethodType.MEDICARE
  );
};

// TODO: find a good spot to move this
export const shouldHideContinueButtonForEligibility = (eligibilityResult: EligibilityCheckResult | undefined) => {
  return (
    eligibilityResult === undefined ||
    eligibilityResult === EligibilityCheckResult.ERROR ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_MEMBER_ID ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_NAME ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_DOB ||
    eligibilityResult === EligibilityCheckResult.INVALID_PARTICIPANT_ID
  );
};

export const getInsuranceSchemaType = (insuranceType: TypeInsuranceFields['insuranceType']): PaymentMethodType => {
  switch (insuranceType) {
    case 'Commercial':
      return PaymentMethodType.COMMERCIAL;
    case 'Medicaid':
      return PaymentMethodType.MEDICAID;
    case 'Medicare':
      return PaymentMethodType.MEDICARE;
  }
};

export const getPayerTypeFromBillingInfo = (info: EvaluationFormType['billingInfo']): PayerTypes => {
  switch (info.type) {
    case PaymentMethodType.COMMERCIAL:
      return PayerTypes.COMMERCIAL;
    case PaymentMethodType.MEDICAID:
      return PayerTypes.MEDICAID;
    case PaymentMethodType.MEDICARE:
      return PayerTypes.MEDICARE;
    case 'privatePay':
      throw new SignupError(`Couldn't map billing info to payer type, info type was ${info.type}`);
    default:
      throw new SignupError(`Unknown billing info type: ${info.type}`);
  }
};
